import { useState, useCallback } from "react";
import { Tabs } from "../discover/Tabs";
import { CountriesTable } from "./CountriesTable";
import { ClubsTable } from "./ClubsTable";
import type { FilterValues } from "../datagrid-configs/types";
import "./OperationsManagementSummaryTableV3.css";

interface OperationsManagementSummaryTableV3Props {
	/** Column visibility override */
	columnVisibility?: Record<string, boolean>;
	/** Storage key for column visibility persistence */
	storageKey?: string;
}

/**
 * OperationsManagementSummaryTableV3 - Operations Management Summary table.
 *
 * This component renders tabs for Countries and Clubs tables.
 * Each tab has its own table component with separate state management.
 *
 * - Countries tab: Displays aggregated data per Country
 * - Clubs tab: Displays data per Club
 */
export function OperationsManagementSummaryTableV3({
	columnVisibility: initialColumnVisibility = {},
	storageKey = "operationsManagementSummary",
}: OperationsManagementSummaryTableV3Props) {
	const [activeTab, setActiveTab] = useState<"countries" | "clubs">("countries");
	const [clubsInitialFilterValues, setClubsInitialFilterValues] = useState<FilterValues | undefined>(undefined);

	// Handle country click - switch to clubs tab and set country filter
	const handleCountryClick = useCallback(
		(countryCode: string) => {
			// Set initial filter values for clubs table
			setClubsInitialFilterValues({
				country: [countryCode], // Multiselect filter expects an array
			});
			// Switch to clubs tab
			setActiveTab("clubs");
		},
		[]
	);

	return (
		<>
			<Tabs
				items={[
					{ label: "Countries", key: "countries" },
					{ label: "Clubs", key: "clubs" },
				]}
				active={activeTab}
				onClick={(key) => {
					setActiveTab(key as "countries" | "clubs");
					// Clear initial filter values when manually switching tabs
					if (key === "countries") {
						setClubsInitialFilterValues(undefined);
					}
				}}
			/>

			{activeTab === "countries" && (
				<CountriesTable
					storageKey={`${storageKey}_countries`}
					initialColumnVisibility={initialColumnVisibility}
					onCountryClick={handleCountryClick}
				/>
			)}

			{activeTab === "clubs" && (
				<ClubsTable
					storageKey={`${storageKey}_clubs`}
					initialColumnVisibility={initialColumnVisibility}
					initialFilterValues={clubsInitialFilterValues}
				/>
			)}
		</>
	);
}

export default OperationsManagementSummaryTableV3;
